import { useMemo, useState } from "react";
import { MuiMaterials } from "../@index.js";
import { comparisonFieldSx } from "./windowChrome/actionButtons.js";
import { getWindowChromeThemeColor } from "./windowChrome/windowChromeTheme.js";
import { withScrollHintMenuProps } from "./windowChrome/ScrollHintPaper.js";

const FILTER_KEYS = [
    "assetTag",
    "partNumber",
    "serialNumber",
    "assetType",
    "manufacturer",
    "model",
    "area",
    "physicalLocation",
    "processFunction",
];

function haystack(part) {
    return FILTER_KEYS.map((key) => String(part?.[key] || "")).join(" ").toLowerCase();
}

/** Type, part number, and location — the line under the instrument and in the menu. */
export function partDetail(part) {
    if (!part) return "";
    const type = String(part.assetType || "").trim();
    const partNumber = String(part.partNumber || "").trim();
    const location = String(part.physicalLocation || "").trim();
    return [
        type,
        partNumber ? `Part ${partNumber}` : "",
        location,
    ].filter(Boolean).join(" · ");
}

/**
 * Instrument tag. Opens a filterable list of the other instruments on this site.
 */
export default function CcrPartSelect({ parts, value, onChange }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [query, setQuery] = useState("");
    const menuText = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const menuBg = getWindowChromeThemeColor("#ffffff", "#2d2d2d");
    const menuHover = getWindowChromeThemeColor("#f5f5f5", "#3d3d3d");
    const open = Boolean(anchorEl);
    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const list = (Array.isArray(parts) ? parts : []).slice().sort((a, b) => (
            String(a?.assetTag || "").localeCompare(String(b?.assetTag || ""), undefined, { numeric: true, sensitivity: "base" })
        ));
        if (!needle) return list;
        return list.filter((part) => haystack(part).includes(needle));
    }, [parts, query]);

    return (
        <>
            <MuiMaterials.Tooltip
                title="Open another instrument on this site. Filter by tag, serial, part number, type, manufacturer, model, area, or location."
                arrow
            >
                <MuiMaterials.Button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                    }}
                    aria-haspopup="menu"
                    aria-expanded={open ? "true" : undefined}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "1.125rem",
                        letterSpacing: 0,
                        lineHeight: 1.2,
                        px: 0.5,
                        py: 0,
                        minWidth: 0,
                        maxWidth: 360,
                        minHeight: "2.4rem",
                        height: "auto",
                        color: getWindowChromeThemeColor("#2d2d2d", "#f4cc8e"),
                        WebkitTextFillColor: getWindowChromeThemeColor("#2d2d2d", "#f4cc8e"),
                        backgroundColor: "transparent",
                        "&:hover": {
                            backgroundColor: getWindowChromeThemeColor("rgba(0,0,0,0.04)", "rgba(255,255,255,0.06)"),
                        },
                    }}
                >
                    <span style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        fontSize: "1.125rem",
                        color: "inherit",
                        WebkitTextFillColor: "inherit",
                    }}>
                        {value || "Instrument"}
                    </span>
                </MuiMaterials.Button>
            </MuiMaterials.Tooltip>
            <MuiMaterials.Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => {
                    setAnchorEl(null);
                    setQuery("");
                }}
                style={{ zIndex: 1600 }}
                sx={{ zIndex: 1600 }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                {...withScrollHintMenuProps({
                    PaperProps: {
                        sx: {
                            backgroundColor: menuBg,
                            backgroundImage: "none",
                            color: menuText,
                            minWidth: 280,
                            maxWidth: "min(90vw, 420px)",
                        },
                    },
                })}
            >
                <MuiMaterials.Box
                    sx={{ px: 1.5, pt: 1, pb: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <MuiMaterials.Tooltip title="Narrow this list by tag, serial, part number, type, manufacturer, model, area, or location." arrow>
                    <MuiMaterials.TextField
                        autoFocus
                        size="small"
                        fullWidth
                        placeholder="Filter tag, serial, part number…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={comparisonFieldSx()}
                    />
                    </MuiMaterials.Tooltip>
                </MuiMaterials.Box>
                {filtered.length === 0 ? (
                    <MuiMaterials.MenuItem disabled sx={{ color: `${menuText} !important` }}>
                        Nothing matches
                    </MuiMaterials.MenuItem>
                ) : filtered.map((part) => {
                    const selected = String(part.assetTag || "") === String(value || "");
                    const detail = partDetail(part);
                    return (
                        <MuiMaterials.MenuItem
                            key={part.assetTag}
                            selected={selected}
                            onClick={() => {
                                setAnchorEl(null);
                                setQuery("");
                                if (!selected && part.assetTag) onChange?.(part.assetTag);
                            }}
                            sx={{
                                color: `${menuText} !important`,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                "&:hover": { backgroundColor: `${menuHover} !important` },
                            }}
                        >
                            <span style={{ fontWeight: 700 }}>{part.assetTag}</span>
                            {detail ? (
                                <span style={{ fontSize: 12, opacity: 0.8 }}>{detail}</span>
                            ) : null}
                        </MuiMaterials.MenuItem>
                    );
                })}
            </MuiMaterials.Menu>
        </>
    );
}
